import React from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from "react-router-dom";
import CustomerLayout from "./components/CustomerLayout";
import ShellLayout from "./components/ShellLayout";
import NotFoundPage from "./components/NotFoundPage";
import VaultThemeProvider from "./theme/VaultThemeProvider";
import SpotterThemeProvider from "./theme/SpotterThemeProvider";
import MetadataImportThemeProvider from "./theme/MetadataImportThemeProvider";
import { ExperienceThemeProvider } from "@cinesys/experience-ui/theme";
import { NavigateBridge } from "./host/NavigateBridge";
import DevHome from "./features/dev/DevHome";
import ColorKitchenSink from "./features/dev/ColorKitchenSink";
import CandidateAssignmentsScreen from "./features/ab-vault/candidate-assignments/CandidateAssignmentsScreen";
import FieldsScreen from "./features/spotter/fields/FieldsScreen";
import FrScreen from "./features/spotter/facialRecognition/FrScreen";
import GamebookScreen from "./features/spotter/gamebook/GamebookScreen";
import RenameScreen from "./features/spotter/rename/RenameScreen";
import UsageScreen from "./features/spotter/usage/UsageScreen";
import MetadataViewerPanel from "./features/cs_metadata_viewer/MetadataViewerPanel";
import CreatePlaceholderPage from "./features/cs_metadata_viewer/components/CreatePlaceholderPage";
import {
  JobViewerApp,
  Wrapper as ItemBrowserWrapper,
  SearchPage as ItemBrowserSearchPage,
  SectionPage as ItemBrowserSectionPage,
  ItemBrowserList,
  DrillInPage,
  type DrillInSourceType,
  RightSidebar,
} from "@cinesys/experience-ui/components";
import PdfViewer from "./features/item-browser/shared/components/PdfViewer";
import InterfaceAccessNotice from "./components/InterfaceAccessNotice";
import { useSessionExpired } from "./hooks/useInterfaceAccess";
import CollectionsBrowserScreen from "./features/cs_collection_browser/CollectionsBrowserScreen";
import ControlRoomScreen from "./features/spotter/controlRoom/ControlRoomScreen";
import MetadataImportOverview from "./features/metadata-import/OverviewScreen";
import MetadataImportProfile from "./features/metadata-import/ProfileScreen";

// DEV-ONLY launcher home (token input + links to each interface route). Never
// reachable in a prod build — mirrors vault-frontend's AppRouter IS_DEV gate
// (apps/vault-frontend/src/AppRouter.tsx), adapted to this app's index route.
const IS_DEV = process.env.NODE_ENV !== "production";

// Feature screens, statically imported (not `React.lazy`'d) — this app ships as a single bundle
// deliberately, so no route ever suspends: a route swap never unmounts/remounts anything else
// still on screen (`ItemBrowserWrapper`'s persistent Sidebar/rail, `ControlRoomScreen`'s
// two-path shared mount, etc.), at the cost of a larger up-front main bundle (no per-route JS
// deferred).
// The verbatim-ported `cs_item_browser` route table — mounted as flat, literal routes below at
// the SAME absolute paths Cantemo's own `Sidebar.tsx`/`BrowseSidebar.tsx` navigate to
// (`/browse`, `/collections`, `/saved_searches`, `/jobs`, `/section/:key`),
// NOT nested under an `item-browser/*` prefix — see `ItemBrowser/routes.ts`'s own doc comment for
// why nesting doesn't work here (no react-router basename on a nested subtree) and isn't needed
// (every one of those paths is already free at the top level). `Wrapper`/`SearchPage` are also
// used standalone for the `home` route: in Cantemo, "Home" and `cs_item_browser`'s own
// default route are the SAME `SearchPage` component (see docs/decisions.md's 2026-09-01 entry) —
// the old, separate `HomeDashboardScreen` this replaces was a from-scratch rebuild of that page.
// Shared full-page drill-in view (Monday 12965030446) — see that component's own doc comment.
// One net-new component, pre-approved (Addison, 2026-09-01: "NO NEW COMPONENTS. EVER." — no
// Cantemo equivalent exists to copy for this exact "nested drill-in" shape).

/**
 * `key={sectionKey}` forces a clean remount of `SectionPage` when navigating from one section to
 * another — react-router-dom v6 doesn't remount `element` on a route path param changing alone
 * (only the pattern must differ), so without the key, state like the in-page search box would
 * survive across sections and incorrectly filter the new section's results. Mirrors the original
 * Cantemo `cs_item_browser` plugin's own `SectionRoute` shape — see `ItemBrowser/routes.ts`'s doc
 * comment for why this table is mounted flat instead of as one nested unit.
 */
const ItemBrowserSectionRoute = () => {
  const { sectionKey } = useParams<{ sectionKey: string }>();
  return <ItemBrowserSectionPage key={sectionKey} />;
};
/**
 * Deliberately no `key={id}` here (unlike `ItemBrowserSectionRoute` above) — `DrillInPage`'s
 * master pane (`CollectionBrowser`) stays mounted across `:id` changes and reads the new `id`
 * reactively (`CollectionBrowserTree.tsx`'s `currentId` prop drives its own active-row highlight
 * directly, no remount needed); the nested (non-full-page-replacement) shape scopes its own
 * remount down to just the results column internally (`DrillInResultsPanel`'s own `key={id}`) —
 * see `DrillInPage.tsx`'s own doc comment. One route element per `DrillInSourceType`
 * (`/collection/:id`, `/saved_search/:id`, `/widget/:id`, and the bare `/collections`,
 * `/saved_searches` landing routes with no `:id` at all — `widget` has no landing route, see its
 * own `<Route>` below) rather than one route matching every shape for a type, since each `:id`
 * route is a genuinely distinct resource — see `DrillInPage.tsx`'s own doc comment. `id` is left
 * as the genuine `string | undefined` `useParams` returns (no `?? ""` coercion) — `DrillInPage`'s
 * `id` prop is itself optional, and treats "no id" as its own real state (the landing shape's
 * placeholder results panel), not an empty string standing in for a real one.
 */
const DrillInRoute = (props: { sourceType: DrillInSourceType }) => {
  const { id } = useParams<{ id: string }>();
  return <DrillInPage sourceType={props.sourceType} id={id} />;
};

/** `search`'s "id" is the query text itself, so unlike every other drill-in type it's read from
 *  `?q=` rather than a path param (Monday 13007597369 — one endpoint, query as a query string,
 *  not baked into the path per-type). Mirrors `DrillInRoute` above, just off `useSearchParams`. */
const SearchDrillInRoute = () => {
  const [searchParams] = useSearchParams();
  // `|| undefined` (not `??`), deliberately: an empty `?q=` and a missing `?q=` both mean "no
  // query yet" the same way `DrillInPage`'s `id?: string` treats any other falsy/absent id.
  return (
    <DrillInPage sourceType="search" id={searchParams.get("q") || undefined} />
  );
};
/** Redirects a `:jobId` route param through unchanged — used below so a `spotter/anchor-review/
 *  :jobId` resume-url already in the wild still lands on the right job's review panel, just
 *  inside the consolidated Control Room page now. Reading `useParams` here (rather than a
 *  static string) is what keeps the redirect job-scoped instead of always bouncing to the bare
 *  list. */
const AnchorReviewToControlRoomRedirect = () => {
  const { jobId } = useParams<{ jobId: string }>();
  return (
    <Navigate
      to={jobId ? `/spotter/control-room/${jobId}` : "/spotter/control-room"}
      replace
    />
  );
};

/**
 * Root SPA router. `basename="/"` — the customer SPA sits at the domain root,
 * unlike vault's `basename="/ui"` (spec §2.2/Appendix C Conflict 3).
 *
 * No session gate here: `index.tsx` mounts the app unconditionally (with or without a URL
 * token) and `CustomerApiService` gates authenticated requests on the boot exchange instead
 * (see its own doc comment). A session dying mid-use and a visitor's initial unauthenticated 401
 * are the same situation from the user's side, so both are handled the SAME way, entirely inside
 * `useInterfaceAccess`, per interface — nothing left for this router to gate on.
 * The index route itself is dev-only chrome now (`DevHome`, gated on `IS_DEV`) — a real build
 * redirects `/` straight to `/home` unconditionally (`LandingPage` is gone, `docs/decisions.md`'s
 * 2026-09-06 entry; the `useReachableSurfaces` launcher-grid hook it used outlived it as
 * `ShellHeader`'s product-switcher data source, and is gone too now that switcher is, this
 * round's 2026-09-07 entry).
 *
 * `CustomerLayout` is mounted as a layout route (renders its children via `<Outlet />`) wrapping
 * every route except the catch-all 404. It is intentionally header-less/nav-less (see its own
 * doc comment) — `ShellLayout`, nested one level further in under the SECOND `CustomerLayout`
 * branch below, is what mounts `ShellHeader` (a darker-than-body background, no product-switcher
 * dropdown, the org's iconik logo, "Powered by Cinesys" — no cross-product chrome) above
 * customer-apps' own baseline surfaces ONLY (`/`, `item-detail/:assetId`, `item-detail/placeholder/
 * new`, `collections-browser` here; `home`/`item-browser`/`browse`/`collections`/etc. in the first
 * branch above). `ab-vault`/`spotter/*`/`metadata-import` routes sit as DIRECT children of that
 * same `CustomerLayout` branch instead, deliberately NOT nested under `ShellLayout` — each is a
 * provisioned interface (every customer org gets exactly ONE) with its own full self-contained
 * header (`AbHeader`/`SpotterHeader`/`MetadataImportHeader`: own mark, `StagingBadge`, org
 * breadcrumb, "Powered by Cinesys") and its own route-scoped theme
 * (`VaultThemeProvider`/`SpotterThemeProvider`/`MetadataImportThemeProvider`), completely
 * unaffected by `ShellHeader`'s redesign by construction — see `docs/decisions.md` for the full
 * history of this split.
 *
 * `/`, `item-detail/:assetId`, `item-detail/placeholder/new`, and `collections-browser` are
 * baseline flag-gated surfaces (plan Phase 2/S3, S15, S18), not provisioned plugin instances —
 * they render in the default ColorProvider/CustomerTheme palette (no theme wrapper) but share
 * `ShellLayout`'s `ShellHeader` chrome. `item-detail/:assetId`/`item-detail/placeholder/new`
 * (`cs_metadata_viewer`) and `collections-browser` (the top-level `cs_collection_browser` route, a
 * different mount from `item-browser`'s own nested copy) are each a DIFFERENT Cantemo plugin, each
 * with its own `createRoot()`/`ColorProvider` mount in the real Cantemo bundle — matching them is
 * the same kind of work, just not this route group's, so they still render in the plain default
 * palette.
 *
 * Every route that renders some part of the ported `cs_item_browser` tree (`home`,
 * `item-browser`, `browse`, `collections`, `saved_searches`, `jobs`,
 * `section/:key`, `collection/:id`, `saved_search/:id`, `widget/:id`) mounts under its
 * OWN `<Route element={<CustomerLayout />}>` branch below, with
 * `ExperienceThemeProvider` wrapping `CustomerLayout` itself rather than just the route content —
 * `CustomerLayout`'s own background (`Shell`, `background: var(--dark060)`) and `ShellLayout`'s
 * header (`ShellHeader` — its own bar is a literal color, darker than `--dark060` in either
 * palette, but its `Crumb`/`StagingBadge` text still reads themed `--dark*`/`--headerCrumb*`
 * tokens) are both ABOVE the per-route `<Outlet />` content in the DOM, so a theme wrapper scoped
 * only to the route element (the previous shape) left both of them rendering in `@cinesys/ui`'s
 * plain default palette no matter what the route itself did. Wrapping the whole `CustomerLayout`
 * instance instead puts every CSS custom property `cinesysExperienceColors` overrides in scope
 * for the header and background too, the same way it was already in scope for the route content.
 * `home` and `item-browser` render the identical `SearchPage` `cs_item_browser` itself uses as its
 * default route (see the `ItemBrowserWrapper` doc comment above), so both need the same palette.
 *
 * Catch-all 404 (`path="*"`, below): any path that doesn't match one of the
 * interface routes renders `NotFoundPage` full-page. This used to fall
 * through to the nested `CustomerLayout` catch-all, which rendered
 * `InterfaceHome` ("No interfaces enabled for this account yet.") for
 * typo'd/stale/unknown paths too — the wrong message for "this route doesn't
 * exist" vs. its real meaning ("your account has no interface provisioned"),
 * which used to only apply at the exact index route (`LandingPage`'s own
 * zero-surfaces state). `LandingPage` is gone (`docs/decisions.md`'s entry on
 * this): a deployed build now redirects `/` straight to `/home` instead.
 */
const AppRouter = () => {
  // Computed here, once, and threaded into `ItemBrowserWrapper` below as props — the package's
  // own `Wrapper.tsx` (Monday S2.5d) can no longer call this app-local hook or render the
  // app-local `InterfaceAccessNotice` directly, see that file's own prop doc comment.
  const sessionExpired = useSessionExpired();
  return (
    <BrowserRouter basename="/">
      {/* Stashes `useNavigate()` for `HostAdapter.navigate` (`host/NavigateBridge.tsx`) — has to
            mount inside `<BrowserRouter>`, where the hook works. */}
      <NavigateBridge />
      <Routes>
        {/* Every route that renders some part of the ported `cs_item_browser` tree gets its OWN
              `CustomerLayout` branch, with `ExperienceThemeProvider` wrapping `CustomerLayout`
              itself (not just the route content) — see the doc comment above for why the header/
              background need the wrapper at this level. */}
        <Route
          element={
            <ExperienceThemeProvider>
              <CustomerLayout />
            </ExperienceThemeProvider>
          }
        >
          {IS_DEV && (
            // Live-verify gallery for `cinesysExperienceColors.ts` — mounted inside
            // `ExperienceThemeProvider` (above) so it renders every raw token + a
            // representative component set with this app's actual override, not `@cinesys/ui`'s
            // stock palette. Dev-only, same IS_DEV gate as DevHome.
            <Route path="dev/colors" element={<ColorKitchenSink />} />
          )}
          <Route element={<ShellLayout />}>
            {/* Back-compat for any bookmarked/shared `/home-dashboard` link from before the
                  rename (docs/decisions.md) — cheap to keep, avoids a dead link. A bare
                  redirect has nothing to render, so it sits outside the shared
                  `ItemBrowserWrapper` layout route below rather than mounting (and instantly
                  discarding) the rail shell for no reason. */}
            <Route
              path="home-dashboard/*"
              element={<Navigate to="/home" replace />}
            />
            {/* Every route below shares ONE mounted `<ItemBrowserWrapper>` (Sidebar + rail
                  panel + `GearMenuHost`) via `<Outlet />`, instead of each route wrapping (and
                  so remounting) its own copy. Before this, `ItemBrowserWrapper` sat INSIDE each
                  leaf route's own `element` — react-router-dom v6 only remounts an `element`
                  when the matched Route PATTERN changes (not on a param-only change, e.g.
                  `/collection/:id` -> `/collection/:id2`), but navigating between two DIFFERENT
                  patterns below (e.g. `/home` -> `/collection/:id`) still tore down and rebuilt
                  Sidebar/the rail panel/`GearMenuHost` every time, even though the rail is
                  meant to be persistent client-side panel state across navigation, not
                  something that only looks persistent by re-reading localStorage on every fresh
                  mount. Addison: navigating to an unloaded page was "re-rendering the entire
                  page, like the header flashes and everything... it should only be loading
                  whatever section it's pulling in" (2026-09-08 interactive session). */}
            <Route
              element={
                <ItemBrowserWrapper
                  sessionExpired={sessionExpired}
                  sessionExpiredNotice={
                    <InterfaceAccessNotice reason="unauthenticated" />
                  }
                  renderRightSidebar={(items) => <RightSidebar items={items} />}
                  pdfViewer={PdfViewer}
                >
                  <Outlet />
                </ItemBrowserWrapper>
              }
            >
              {/* Home = cs_item_browser's own default route (SearchPage) — see the
                    `ItemBrowserWrapper`/`ItemBrowserSearchPage` doc comment above. `item-browser`
                    (bare) renders the identical page — a pre-existing customer-apps alias
                    (`customerAppsHostAdapter.ts` opens it), not a Cantemo path. */}
              {/* Trailing `/*` keeps a deeper path under either mount (a stale or hand-typed
                    `/home/<anything>`) resolving to `SearchPage` rather than falling through to
                    the top-level 404. `SearchPage` renders no nested `<Routes>` of its own — it
                    switches between its AI-results and widget-carousel views on component state
                    — so nothing here depends on the splat to reach a sub-view. */}
              <Route path="home/*" element={<ItemBrowserSearchPage />} />
              <Route
                path="item-browser/*"
                element={<ItemBrowserSearchPage />}
              />
              {/* The rest of cs_item_browser's own route table, mounted flat at Cantemo's own
                    literal absolute paths — see `ItemBrowser/routes.ts`'s doc comment for why
                    flat rather than nested under an `item-browser/*` prefix. `ItemBrowserList`
                    owns both the bare "no storage picked yet" state and any deeper `:storageId/*`
                    path itself (`useParams()["*"]`), so all three route shapes below point at the
                    same lazy component rather than needing separate screens. */}
              <Route path="browse" element={<ItemBrowserList />} />
              <Route path="browse/:storageId" element={<ItemBrowserList />} />
              <Route path="browse/:storageId/*" element={<ItemBrowserList />} />
              {/* Landing routes (no `:id`) point at the SAME `DrillInRoute`/`DrillInPage` shape
                    the `:id` routes below use, just with no id — one unified
                    narrow-list-plus-results-panel page per entity type, landing-page-first. See
                    `DrillInPage.tsx`'s own doc comment for the no-id (placeholder results panel,
                    never auto-selected) behavior. */}
              <Route
                path="collections"
                element={<DrillInRoute sourceType="collection" />}
              />
              <Route
                path="saved_searches"
                element={<DrillInRoute sourceType="saved_search" />}
              />
              {/* iconik-only (Monday 13292999401) — same landing-route-first shape as
                    Collections/Saved Searches above, see `MappedCollectionsSidebar.tsx`'s own doc
                    comment for the feature itself. */}
              <Route
                path="mapped_collections"
                element={<DrillInRoute sourceType="mapped_collection" />}
              />
              <Route path="jobs/*" element={<JobViewerApp />} />
              <Route
                path="section/:sectionKey"
                element={<ItemBrowserSectionRoute />}
              />
              {/* Shared full-page drill-in view (Monday 12965030446) — path-based, mirroring
                    resource type, per docs/decisions.md's 2026-09-03 entry. Reached from
                    `CollectionBrowserTree.tsx`'s row links, `ItemTitle.tsx`/`ResultCard.tsx`'s
                    collection-type result cards, and
                    (as a full-page replacement, `?origin=widget&return=...`)
                    `Widget.tsx`'s "View more". */}
              <Route
                path="collection/:id"
                element={<DrillInRoute sourceType="collection" />}
              />
              <Route
                path="saved_search/:id"
                element={<DrillInRoute sourceType="saved_search" />}
              />
              <Route
                path="mapped_collection/:id"
                element={<DrillInRoute sourceType="mapped_collection" />}
              />
              {/* `widget`/`search` have no bare landing route the way the other two do — there's
                    no "browse all widgets"/"browse all searches" list to land on. `search` has
                    no `:id` segment at all (Monday 13007597369 — a single query-string
                    endpoint, not one path per search type): the query text itself (Monday
                    13000223024 — the Home page header-search's "Show all results") is read from
                    `?q=` instead, so an empty/missing query still matches this route rather than
                    404ing. Always a full-page replacement (see `DrillInPage.tsx`'s
                    `isFullPageReplacement`), same as `widget`'s own `?origin=widget&return=...`. */}
              <Route path="search" element={<SearchDrillInRoute />} />
              <Route
                path="widget/:id"
                element={<DrillInRoute sourceType="widget" />}
              />
            </Route>
          </Route>
        </Route>
        {/* Every route below renders in the plain default `@cinesys/ui` `ColorProvider` palette
              (no `ExperienceThemeProvider`) — the baseline flag-gated surfaces, `cs_metadata_
              viewer`/`cs_collection_browser` (each a different Cantemo plugin from
              `cs_item_browser`), and every provisioned interface with its own route-scoped
              theme (`VaultThemeProvider`/`SpotterThemeProvider`/`MetadataImportThemeProvider`). */}
        <Route element={<CustomerLayout />}>
          <Route element={<ShellLayout />}>
            <Route
              index
              element={IS_DEV ? <DevHome /> : <Navigate to="/home" replace />}
            />
            {/* Phase 2 baseline surfaces — flag-gated (not provisioned plugin instances), so no
                  VaultThemeProvider/SpotterThemeProvider wrapper: they render in the default
                  ColorProvider/CustomerTheme palette. Each route's own screen calls its own
                  backend route, which carries the `enabled` verdict in its response
                  (`FeatureGate` renders on it) rather than this router consulting
                  `enabledInterfaces` the way ab-vault/spotter do below. */}
            <Route
              path="item-detail/:assetId"
              element={<MetadataViewerPanel />}
            />
            <Route
              path="item-detail/placeholder/new"
              element={<CreatePlaceholderPage />}
            />
            <Route
              path="collections-browser"
              element={<CollectionsBrowserScreen />}
            />
          </Route>
          {/* AB Vault / Spotter / Metadata Import are each a provisioned interface, not a
                ShellLayout baseline surface — every customer org is provisioned exactly ONE of
                these, and each renders its OWN full self-contained header (`AbHeader`/
                `SpotterHeader`/`MetadataImportHeader`: own mark, `StagingBadge`, org breadcrumb,
                "Powered by Cinesys") rather than `ShellHeader`'s chrome. See docs/decisions.md's
                entry on the Aug 28/31 ShellHeader unification being reverted for these three. */}
          <Route
            path="ab-vault/candidate-assignments"
            element={
              <VaultThemeProvider>
                <CandidateAssignmentsScreen />
              </VaultThemeProvider>
            }
          />
          {/* M4: Spotter tool (spec §8.6) — the unified Fields tab (Field Mapping + Glossary &
                AI Context), wrapped in SpotterThemeProvider (the `--spotter*` slate/amber
                palette, kept separate from the AB `--ab*` teal palette above) and rendering its
                own SpotterHeader. Statically imported (see the import-list doc comment above),
                so no per-route Suspense is needed anymore — `SpotterHeader` never blanks out
                waiting on a chunk. */}
          <Route
            path="spotter/fields"
            element={
              <SpotterThemeProvider>
                <FieldsScreen />
              </SpotterThemeProvider>
            }
          />
          {/* Facial Recognition tab — FR-person -> destination-field routing, its own
                independent store/save path (`frMapping/service.ts`), separate from Fields'
                Mode picker. Same SpotterThemeProvider as above. */}
          <Route
            path="spotter/facial-recognition"
            element={
              <SpotterThemeProvider>
                <FrScreen />
              </SpotterThemeProvider>
            }
          />
          {/* Gamebook tab — one row per week for the org's own NFL club matchup (completed
                box score or opponent preview + a "Generate" trigger). Same SpotterThemeProvider
                as above. */}
          <Route
            path="spotter/gamebook"
            element={
              <SpotterThemeProvider>
                <GamebookScreen />
              </SpotterThemeProvider>
            }
          />
          {/* File Rename tab — per-category filename templates + rename options. Same
                SpotterThemeProvider as above. */}
          <Route
            path="spotter/rename"
            element={
              <SpotterThemeProvider>
                <RenameScreen />
              </SpotterThemeProvider>
            }
          />
          {/* Usage tab — consumption-only billing dashboard (practice/game hours for the active
                period). Same SpotterThemeProvider as above. */}
          <Route
            path="spotter/usage"
            element={
              <SpotterThemeProvider>
                <UsageScreen />
              </SpotterThemeProvider>
            }
          />
          {/* Anchor Review is not its own route/screen — these two paths still resolve (a
                resume-url already in the wild, or a stale bookmark, points at them) but only
                ever redirect into the Control Room route below, job-scoped when a `:jobId` was
                present. See `AnchorReviewToControlRoomRedirect`'s own doc comment. */}
          <Route
            path="spotter/anchor-review"
            element={<AnchorReviewToControlRoomRedirect />}
          />
          <Route
            path="spotter/anchor-review/:jobId"
            element={<AnchorReviewToControlRoomRedirect />}
          />
          {/* Control Room tab — read side of the status-push pipeline: every job's live stage,
                org-wide. ALSO the Films Ground/Top Films Anchor Review surface — a Films
                Ground/Top Films row opens its review panel in place at the SAME route with an
                optional `:jobId` (`ControlRoomScreen.tsx`'s own doc comment covers the split).
                Both paths point at the SAME component reference so React preserves the
                mounted screen (header, jobs table/panel toggle) across the two. Same
                SpotterThemeProvider as every other Spotter tab. */}
          <Route
            path="spotter/control-room"
            element={
              <SpotterThemeProvider>
                <ControlRoomScreen />
              </SpotterThemeProvider>
            }
          />
          <Route
            path="spotter/control-room/:jobId"
            element={
              <SpotterThemeProvider>
                <ControlRoomScreen />
              </SpotterThemeProvider>
            }
          />
          {/* Metadata Import & Enrichment — a reusable Customer Portal interface, sibling of
                AB Vault / Spotter. Its own MetadataImportThemeProvider (`--mi*` palette) and its
                own MetadataImportHeader. Overview = the profiles list; `:profileId` = the
                profile detail (six in-page tabs). */}
          <Route
            path="metadata-import"
            element={
              <MetadataImportThemeProvider>
                <MetadataImportOverview />
              </MetadataImportThemeProvider>
            }
          />
          <Route
            path="metadata-import/:profileId"
            element={
              <MetadataImportThemeProvider>
                <MetadataImportProfile />
              </MetadataImportThemeProvider>
            }
          />
          {/* M3+: additional provisioned-interface routes go here, nested under CustomerLayout
                (own theme + header). A new baseline flag-gated surface goes in the ShellLayout
                block above instead. */}
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
